if (!customElements.get('cabinet-product')) {
  customElements.define('cabinet-product', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.setupThumbnailDrag();
      this.requestNumber = 0;
      this.addEventListener('click', (event) => {
        if (this.getAttribute('aria-busy') === 'true' && event.target.closest('.product-form__checkout')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-value-id]')) this.changeVariant(event.target);
      });
      this.addEventListener('submit', (event) => {
        if (this.getAttribute('aria-busy') === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      this.addEventListener('click', (event) => this.handleClick(event));
      this.addEventListener('keydown', (event) => {
        if (!event.target.closest('[data-gallery]')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.stepGallery(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      this.successfulSelection = this.selection();
      this.showMedia(this.querySelector('[data-media-id]:not([hidden])')?.dataset.mediaId);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    setupThumbnailDrag() {
      const strip = this.querySelector('.fh-cabinet__thumbnails');
      if (!strip) return;
      let drag;
      let suppressClick = false;
      strip.addEventListener('dragstart', (event) => event.preventDefault());
      strip.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        suppressClick = false;
        drag = { id: event.pointerId, x: event.clientX, left: strip.scrollLeft, moved: false };
      });
      strip.addEventListener('pointermove', (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        const distance = event.clientX - drag.x;
        if (!drag.moved && Math.abs(distance) < 6) return;
        if (!drag.moved) {
          drag.moved = true;
          strip.setPointerCapture(event.pointerId);
          strip.classList.add('is-dragging');
        }
        event.preventDefault();
        strip.scrollLeft = drag.left - distance;
      });
      const endDrag = (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        suppressClick = drag.moved;
        drag = null;
        strip.classList.remove('is-dragging');
        if (strip.hasPointerCapture(event.pointerId)) strip.releasePointerCapture(event.pointerId);
      };
      strip.addEventListener('pointerup', endDrag);
      strip.addEventListener('pointercancel', endDrag);
      strip.addEventListener('lostpointercapture', endDrag);
      strip.addEventListener('pointerleave', (event) => {
        if (drag && !drag.moved) endDrag(event);
      });
      strip.addEventListener('click', (event) => {
        if (!suppressClick || event.detail === 0) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);
    }

    selection() {
      return [...this.querySelectorAll('fieldset[data-option-position]')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((field) => field.querySelector('input:checked')?.dataset.optionValueId)
        .filter(Boolean);
    }

    lockCheckout(locked) {
      this.querySelectorAll('.product-form__checkout').forEach((checkout) => {
        checkout.toggleAttribute('inert', locked);
        if (locked) checkout.setAttribute('aria-disabled', 'true');
        else checkout.removeAttribute('aria-disabled');
      });
    }

    async changeVariant(input) {
      const requestNumber = ++this.requestNumber;
      this.controller?.abort();
      this.controller = new AbortController();
      const quantity = this.querySelector('[name="quantity"]')?.value;
      const focusedId = input.id;
      const field = input.closest('fieldset');
      const scrollLeft = field?.querySelector('.fh-cabinet__option-values')?.scrollLeft || 0;
      const position = field?.dataset.optionPosition;
      const url = new URL(window.location.href);
      const destination = input.dataset.productUrl || this.dataset.productUrl;
      const nextPath = new URL(destination, url.origin).pathname;
      if (nextPath !== new URL(this.dataset.productUrl, url.origin).pathname) url.pathname = nextPath;
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', this.selection().join(','));
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.setAttribute('aria-busy', 'true');
      this.lockCheckout(true);
      const buy = this.querySelector('[name="add"]');
      if (buy) buy.disabled = true;
      this.querySelector('[data-selection-error]').hidden = true;
      try {
        const response = await fetch(url.toString(), { signal: this.controller.signal });
        if (!response.ok) throw new Error('Variant section request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const freshRoot = document.querySelector('cabinet-product');
        const info = freshRoot?.querySelector('[data-cabinet-info]');
        const state = JSON.parse(info?.querySelector('[data-cabinet-selection]')?.textContent || 'null');
        if (!info || !state) throw new Error('Missing variant content');
        if (requestNumber !== this.requestNumber) return;
        if (freshRoot.dataset.productUrl !== this.dataset.productUrl) {
          url.searchParams.delete('section_id');
          window.location.assign(url.toString());
          return;
        }
        this.querySelector('[data-cabinet-info]').replaceWith(info);
        this.lockCheckout(true);
        const quantityInput = this.querySelector('[name="quantity"]');
        if (quantityInput && quantity) {
          const min = Number(quantityInput.min) || 1;
          const step = Number(quantityInput.step) || 1;
          const max = Number(quantityInput.max) || Infinity;
          const next = Math.max(min, Math.min(max, Number(quantity)));
          quantityInput.value = String(min + Math.floor((next - min) / step) * step);
        }
        this.successfulSelection = this.selection();
        this.querySelector(`[data-option-position="${position}"] .fh-cabinet__option-values`)?.scrollTo({ left: scrollLeft });
        this.querySelector(`[id="${CSS.escape(focusedId)}"]`)?.focus({ preventScroll: true });
        if (state.valid && state.mediaId) this.showMedia(String(state.mediaId));
        url.searchParams.delete('section_id');
        if (state.valid && state.id) {
          url.searchParams.delete('option_values');
          url.searchParams.set('variant', state.id);
        }
        window.history.replaceState({}, '', url.toString());
        this.dispatchEvent(new CustomEvent('cabinet:variant-change', { bubbles: true, detail: state }));
      } catch (error) {
        if (error.name === 'AbortError' || requestNumber !== this.requestNumber) return;
        this.querySelectorAll('[data-option-value-id]').forEach((radio) => {
          radio.checked = this.successfulSelection.includes(radio.dataset.optionValueId);
        });
        const state = JSON.parse(this.querySelector('[data-cabinet-selection]').textContent);
        const restoredBuy = this.querySelector('[name="add"]');
        if (restoredBuy) restoredBuy.disabled = !state.valid || !state.available;
        this.querySelector('[data-selection-error]').hidden = false;
      } finally {
        if (requestNumber === this.requestNumber) {
          this.removeAttribute('aria-busy');
          this.lockCheckout(false);
        }
      }
    }

    handleClick(event) {
      const thumbnail = event.target.closest('[data-media-target]');
      if (thumbnail) this.showMedia(thumbnail.dataset.mediaTarget);
      const arrow = event.target.closest('[data-gallery-step]');
      if (arrow) this.stepGallery(Number(arrow.dataset.galleryStep));
      const quantityButton = event.target.closest('[data-quantity-step]');
      if (quantityButton) {
        const input = this.querySelector('[name="quantity"]');
        if (Number(quantityButton.dataset.quantityStep) > 0) input.stepUp();
        else input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (event.target.closest('[data-open-measure]')) this.querySelector('[data-measure-dialog]').showModal();
      if (event.target.closest('[data-close-dialog]')) event.target.closest('dialog').close();
      if (event.target.matches('dialog')) {
        const rect = event.target.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
      }
      if (event.target.closest('[data-zoom]')) {
        const slide = this.querySelector('[data-media-id]:not([hidden])');
        if (slide?.dataset.image) {
          const image = this.querySelector('[data-zoom-image]');
          image.src = slide.dataset.image;
          image.alt = slide.querySelector('img')?.alt || this.querySelector('h1').textContent;
          this.querySelector('[data-zoom-dialog]').showModal();
        }
      }
      const play = event.target.closest('[data-play-video]');
      if (play) {
        const template = play.parentElement.querySelector('[data-external-video]');
        play.after(template.content.cloneNode(true));
        play.hidden = true;
      }
    }

    stepGallery(step) {
      const slides = [...this.querySelectorAll('[data-media-id]')];
      if (!slides.length) return;
      const active = Math.max(0, slides.findIndex((slide) => !slide.hidden));
      this.showMedia(slides[(active + step + slides.length) % slides.length].dataset.mediaId);
    }

    showMedia(id) {
      const slides = [...this.querySelectorAll('[data-media-id]')];
      const selected = slides.find((slide) => slide.dataset.mediaId === String(id));
      if (!selected) return;
      slides.forEach((slide) => {
        slide.hidden = slide !== selected;
        if (slide.hidden) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('iframe').forEach((iframe) => iframe.remove());
          const play = slide.querySelector('[data-play-video]');
          if (play) play.hidden = false;
        }
      });
      this.querySelectorAll('[data-media-target]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === String(id)));
      });
      const activeThumb = this.querySelector(`[data-media-target="${id}"]`);
      const strip = activeThumb?.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) strip.scrollLeft = activeThumb.offsetLeft - strip.offsetLeft;
      if (strip && strip.scrollHeight > strip.clientHeight) strip.scrollTop = activeThumb.offsetTop - strip.offsetTop;
      const count = this.querySelector('[data-gallery-count]');
      if (count) count.textContent = `${String(slides.indexOf(selected) + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      const zoom = this.querySelector('[data-zoom]');
      if (zoom) zoom.hidden = !selected.dataset.image;
    }
  });
}

if (!customElements.get('cabinet-related')) {
  customElements.define('cabinet-related', class extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-load-recommendations') || this.observer) return;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.load();
        }
      }, { rootMargin: '400px' });
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    async load() {
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        const current = new URL(window.location.href);
        ['preview_theme_id', 'view'].forEach((key) => {
          if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
        });
        const response = await fetch(url.toString());
        if (!response.ok) return;
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = document.querySelector('[data-related-content]');
        if (content && this.isConnected) {
          this.querySelector('[data-related-content]').replaceWith(content);
          this.hidden = !content.querySelector('.fh-cabinet-related__header');
        }
      } catch (_) {}
    }
  });
}
